import api from '@services/api';
import {
  CreateRecurringBuyRequest,
  RecurringBuyApi,
  RecurringBuyCreateResponse,
  RecurringBuyResponse,
} from '@services/generated';
import { rootApi } from '@store/root-api';
import appHelpers from '@utils/app-urls';
import { handleErrors } from 'services/helpers';
import { delay } from 'utils/delay';

const API_URL = appHelpers.getEndpointUrl();

const recurringBuyClient = new RecurringBuyApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

const recurringBuyApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getRecurringBuys: builder.query<RecurringBuyResponse[], void>({
      queryFn: () => handleErrors(() => recurringBuyClient.getRecurringBuys()),
      providesTags: [{ type: 'RecurringBuys', id: 'LIST' }],
    }),
    getRecurringBuyById: builder.query<RecurringBuyResponse, string>({
      queryFn: id =>
        handleErrors(() => recurringBuyClient.getRecurringBuyById(id)),
      providesTags: (result, error, id) => [{ type: 'RecurringBuys', id }],
    }),

    createRecurringBuy: builder.mutation<
      RecurringBuyCreateResponse,
      CreateRecurringBuyRequest
    >({
      queryFn: recurringBuy =>
        handleErrors(async () => {
          return await recurringBuyClient.createRecurringBuy(recurringBuy);
        }),
      invalidatesTags: [{ type: 'RecurringBuys', id: 'LIST' }],
    }),
    pauseRecurringBuy: builder.mutation<void, string>({
      queryFn: id =>
        handleErrors(() => recurringBuyClient.pauseRecurringBuy(id)),
      // This could be done more granularly to invalidate just the recurring buys that changed
      // https://redux-toolkit.js.org/rtk-query/usage/mutations#advanced-mutations-with-revalidation
      invalidatesTags: (result, error, id) => [
        { type: 'RecurringBuys', id },
        { type: 'RecurringBuys', id: 'LIST' },
      ],
      // Example of pessimistic update. Not strictly necessary but i thought it would be cool to show as an example
      // https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates#pessimistic-updates
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            recurringBuyApi.util.updateQueryData(
              'getRecurringBuyById',
              id,
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
        } catch { }
      },
    }),
    resumeRecurringBuy: builder.mutation<void, string>({
      queryFn: id =>
        handleErrors(async () => {
          var res = recurringBuyClient.resumeRecurringBuy(id);
          // We add a delay to make sure we can see the beautiful loading screen
          await delay(3000);
          return res;
        }),
      invalidatesTags: (result, error, id) => [
        { type: 'RecurringBuys', id },
        { type: 'RecurringBuys', id: 'LIST' },
      ],
    }),
    cancelRecurringBuy: builder.mutation<void, string>({
      queryFn: id =>
        handleErrors(() => recurringBuyClient.cancelRecurringBuy(id)),
      invalidatesTags: (result, error, id) => [
        { type: 'RecurringBuys', id },
        { type: 'RecurringBuys', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useCancelRecurringBuyMutation,
  useCreateRecurringBuyMutation,
  useGetRecurringBuyByIdQuery,
  useGetRecurringBuysQuery,


  usePauseRecurringBuyMutation,
  useResumeRecurringBuyMutation,

} = recurringBuyApi;
